import { cx } from '../cx';

/** Um lado de um jogo no bracket. */
export type BracketSide = { name: string; seed: number } | null;

export type BracketViewMatch = {
  id: string;
  a: BracketSide;
  b: BracketSide;
  winner: 'a' | 'b' | null;
  /** Um lado vazio desde o inicio: o outro passou sem jogar. */
  bye: boolean;
  scoreA?: number | null;
  scoreB?: number | null;
  /** Texto pequeno por baixo (ex.: "W.O."). */
  note?: string | null;
  /** A hora do jogo, ja formatada (ex.: "sáb. 15:30"), por cima. */
  when?: string | null;
};

export type BracketViewProps = {
  rounds: { title: string; matches: BracketViewMatch[] }[];
  thirdPlace?: BracketViewMatch | null;
  /** Com isto, cada jogo com os dois lados definidos e um botao (back-office). */
  onMatchClick?: ((id: string) => void) | undefined;
  className?: string;
};

function Side({
  side,
  won,
  lost,
  score,
  bye,
}: {
  side: BracketSide;
  won: boolean;
  lost: boolean;
  score: number | null | undefined;
  bye: boolean;
}) {
  return (
    <span
      className={cx(
        'flex items-center gap-2 px-2 py-1.5',
        won && 'font-bold text-gu-ink',
        lost && 'text-gu-ink-muted',
      )}
    >
      <span className="w-5 shrink-0 text-right text-caption text-gu-ink-muted">
        {side ? side.seed : ''}
      </span>
      <span className="min-w-0 flex-1 truncate" title={side?.name}>
        {side ? (
          side.name
        ) : bye ? (
          <em className="text-gu-ink-muted">bye</em>
        ) : (
          <span className="text-gu-ink-muted">A definir</span>
        )}
      </span>
      {score !== undefined && score !== null ? (
        <span className="shrink-0 tabular-nums">{score}</span>
      ) : null}
    </span>
  );
}

function MatchBox({
  match,
  onMatchClick,
}: {
  match: BracketViewMatch;
  onMatchClick?: ((id: string) => void) | undefined;
}) {
  const body = (
    <>
      {match.when ? (
        <span className="block border-b border-gu-line px-2 py-1 text-caption text-gu-ink-muted">
          {match.when}
        </span>
      ) : null}
      <Side
        side={match.a}
        won={match.winner === 'a'}
        lost={match.winner === 'b'}
        score={match.scoreA}
        bye={match.bye}
      />
      <span className="block border-t border-gu-line" aria-hidden="true" />
      <Side
        side={match.b}
        won={match.winner === 'b'}
        lost={match.winner === 'a'}
        score={match.scoreB}
        bye={match.bye}
      />
      {match.note ? (
        <span className="block border-t border-gu-line px-2 py-1 text-caption text-gu-ink-muted">
          {match.note}
        </span>
      ) : null}
    </>
  );
  const box = 'flex w-full flex-col rounded border border-gu-line bg-gu-ground text-left text-body';
  const playable = onMatchClick && match.a !== null && match.b !== null && !match.bye;
  if (playable) {
    const label = `${match.a?.name ?? ''} contra ${match.b?.name ?? ''}`;
    return (
      <button
        type="button"
        aria-label={label}
        className={cx(
          box,
          'hover:border-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
        )}
        onClick={() => onMatchClick(match.id)}
      >
        {body}
      </button>
    );
  }
  return <div className={cx(box, match.bye && 'opacity-60')}>{body}</div>;
}

/**
 * Bracket de eliminacao simples: uma coluna por ronda, da esquerda para a
 * direita, com scroll horizontal so dentro da caixa (nunca na pagina).
 */
export function BracketView({ rounds, thirdPlace, onMatchClick, className }: BracketViewProps) {
  return (
    <div className={cx('flex flex-col gap-6', className)}>
      <div className="overflow-x-auto pb-2">
        <ol className="flex min-w-max gap-4">
          {rounds.map((r) => (
            <li key={r.title} className="flex w-52 flex-col gap-3">
              <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
                {r.title}
              </h3>
              <ul className="flex flex-1 flex-col justify-around gap-3">
                {r.matches.map((m) => (
                  <li key={m.id}>
                    <MatchBox match={m} onMatchClick={onMatchClick} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
      {thirdPlace ? (
        <section className="flex w-52 flex-col gap-3">
          <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
            3.º e 4.º lugar
          </h3>
          <MatchBox match={thirdPlace} onMatchClick={onMatchClick} />
        </section>
      ) : null}
    </div>
  );
}

export type GroupStageRow = {
  name: string;
  seed: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  scoreFor: number;
  scoreAgainst: number;
  points: number;
  /** Nos lugares que passam (fica destacado). */
  qualifying: boolean;
  disqualified?: boolean;
};

export type GroupStageGroup = {
  name: string;
  standings: GroupStageRow[];
  rounds: { title: string; matches: BracketViewMatch[] }[];
};

export type GroupStageProps = {
  groups: GroupStageGroup[];
  /** Com isto, cada jogo e um botao (back-office). */
  onMatchClick?: ((id: string) => void) | undefined;
  className?: string;
};

const diff = (r: GroupStageRow) => {
  const d = r.scoreFor - r.scoreAgainst;
  return d > 0 ? `+${String(d)}` : String(d);
};

/**
 * Fase de grupos: por grupo, a tabela (J, V, E, D, diferenca, pontos) e os
 * jogos de cada jornada. Os lugares que passam ficam destacados. A tabela tem
 * scroll horizontal so dentro da caixa.
 */
export function GroupStage({ groups, onMatchClick, className }: GroupStageProps) {
  return (
    <div className={cx('grid gap-6 lg:grid-cols-2', className)}>
      {groups.map((g) => (
        <section
          key={g.name}
          aria-label={g.name}
          className="flex min-w-0 flex-col gap-3 rounded border border-gu-line p-3"
        >
          <h3 className="font-sub text-label font-bold uppercase">{g.name}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-body">
              <thead>
                <tr className="text-caption text-gu-ink-muted">
                  <th scope="col" className="w-6 text-left font-normal">
                    #
                  </th>
                  <th scope="col" className="text-left font-normal">
                    Participante
                  </th>
                  <th scope="col" className="w-8 font-normal" title="Jogos">
                    J
                  </th>
                  <th scope="col" className="w-8 font-normal" title="Vitórias">
                    V
                  </th>
                  <th scope="col" className="w-8 font-normal" title="Empates">
                    E
                  </th>
                  <th scope="col" className="w-8 font-normal" title="Derrotas">
                    D
                  </th>
                  <th scope="col" className="w-10 font-normal" title="Diferença">
                    +/-
                  </th>
                  <th scope="col" className="w-10 font-normal" title="Pontos">
                    Pts
                  </th>
                </tr>
              </thead>
              <tbody>
                {g.standings.map((r, i) => (
                  <tr
                    key={r.name}
                    className={cx(
                      'border-t border-gu-line text-center tabular-nums',
                      r.qualifying && 'font-bold',
                      r.disqualified && 'text-gu-ink-muted line-through',
                    )}
                  >
                    <td className="text-left">
                      <span
                        className={cx(
                          'inline-block w-5 border-l-4 pl-1',
                          r.qualifying ? 'border-gu-red' : 'border-transparent',
                        )}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td className="max-w-0 truncate py-1 text-left" title={r.name}>
                      {r.name}
                      {r.qualifying ? <span className="sr-only"> (passa)</span> : null}
                    </td>
                    <td>{r.played}</td>
                    <td>{r.won}</td>
                    <td>{r.drawn}</td>
                    <td>{r.lost}</td>
                    <td>{diff(r)}</td>
                    <td>{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ol className="flex flex-col gap-3">
            {g.rounds.map((r) => (
              <li key={r.title} className="flex flex-col gap-2">
                <h4 className="text-caption font-bold uppercase text-gu-ink-muted">{r.title}</h4>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {r.matches.map((m) => (
                    <li key={m.id}>
                      <MatchBox match={m} onMatchClick={onMatchClick} />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
